import { FC, useCallback, useMemo, useState } from 'react';
import { subMinutes, subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  DateRange,
  DateRangePicker,
  Dialog,
  SelectRangeEventHandler,
} from '@robocorp/components';
import { addDays } from '@robocorp/robocloud-common/lib/utils/timeUtils';
import { useCurrentOrganization } from '~/queries';
import { getStartOfDayInTimezone } from '~/utils/timeHelper';

interface Props {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  dateRange?: DateRange;
  dateRangeDayLimit?: number;
  onSelect?: (range: DateRange | undefined) => void;
}

type DialogContent = Pick<Props, 'dateRange' | 'onSelect' | 'onClose' | 'dateRangeDayLimit'>;

const DateRangeFilterMenuDialogContent: FC<DialogContent> = ({
  dateRange,
  onSelect,
  onClose,
  dateRangeDayLimit,
}) => {
  const { t } = useTranslation();

  const { data: currentOrganization } = useCurrentOrganization();

  const { now, disabledDays, weekStartsOn } = useMemo(() => {
    const timezone = currentOrganization?.details.timezone ?? 'local';
    const weekStartsOnLocal = currentOrganization?.details.timeFormat === '12' ? 0 : 1;
    const nowZoned = getStartOfDayInTimezone(new Date(), timezone);
    let disabled;
    if (dateRangeDayLimit) {
      disabled = (d: Date) => d > nowZoned || d < addDays(nowZoned, dateRangeDayLimit * -1);
    } else {
      disabled = (d: Date) => d > nowZoned;
    }
    return {
      now: nowZoned,
      disabledDays: disabled,
      weekStartsOn: weekStartsOnLocal as 0 | 1,
    };
  }, [
    currentOrganization?.details.timeFormat,
    currentOrganization?.details.timezone,
    dateRangeDayLimit,
  ]);

  const [range, setRange] = useState<DateRange | undefined>(dateRange);

  const handleSetRange = useCallback<Exclude<SelectRangeEventHandler, undefined>>((nextRange) => {
    if (nextRange && !nextRange.to) {
      setRange({ ...nextRange, to: nextRange.from });
      return;
    }
    setRange(nextRange);
  }, []);

  const handleApply = useCallback(() => {
    // normalize the range to UTC
    const from =
      range && range.from ? subMinutes(range.from, range.from.getTimezoneOffset()) : undefined;
    const to = range && range.to ? subMinutes(range.to, range.to.getTimezoneOffset()) : undefined;
    onSelect?.(range && { from, to });
    onClose();
  }, [onClose, onSelect, range]);

  // the range picker will show the next month in the right most calendar, but it will always will be disabled
  // as we don't have any data to show in the future
  const defaultMonth = useMemo(() => {
    const date = dateRange?.to ?? now;
    return subMonths(date, 1);
  }, [dateRange?.to, now]);

  return (
    <>
      <Dialog.Content>
        <Box display="flex" alignItems="center" justifyContent="center">
          <DateRangePicker
            defaultMonth={defaultMonth}
            selected={range}
            onSelect={handleSetRange}
            disabled={disabledDays}
            toMonth={now}
            today={now}
            weekStartsOn={weekStartsOn}
          />
        </Box>
      </Dialog.Content>
      <Dialog.Actions>
        <Button onClick={handleApply} disabled={!range || !range.to}>
          {t('apply')}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
      </Dialog.Actions>
    </>
  );
};

export const TimeRangeDialog: FC<Props> = ({
  title,
  dateRange,
  isOpen,
  onClose,
  onSelect,
  dateRangeDayLimit,
}) => {
  return (
    <Dialog open={isOpen} onClose={onClose} width="620px">
      <Dialog.Header>
        <Dialog.Header.Title title={title} />
      </Dialog.Header>
      {isOpen && (
        <DateRangeFilterMenuDialogContent
          dateRange={dateRange}
          onSelect={onSelect}
          onClose={onClose}
          dateRangeDayLimit={dateRangeDayLimit}
        />
      )}
    </Dialog>
  );
};
